import { useParams, useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { MetricCard } from "../../../components/PromotionIQ/MetricCard";
import { getRetailerById, formatPercent, formatCurrency } from "../../../data/PromotionIQ/PromotionData";
import { ArrowLeft, Building2, Calendar, Lock, DollarSign, RefreshCcw } from "lucide-react";
import React from "react";

const RetailerEconomics = () => {
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const retailer = getRetailerById(retailerId || "R1");

  if (!retailer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Retailer not found</p>
        <button className="btn-athena mt-4" onClick={() => navigate("/promotioniq/retailer-investment")}>
          Back to Retailers
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/promotioniq/retailer-investment")}
          className="flex h-10 w-10 items-center justify-center border border-border hover:bg-muted transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <ScreenHeader
          title={`${retailer.name} Economics Profile`}
          question="How does this retailer actually make money?"
          className="mb-0"
        />
      </div>

      {/* Economic Model Overview */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-primary" />
            <h3 className="section-header">Economic Model</h3>
          </div>
          <div className="text-center p-8 border border-primary/30 bg-primary/5 flex-grow flex flex-col justify-center">
            <p className="text-3xl font-bold text-primary">{retailer.economic_model}</p>
            <p className="text-muted-foreground mt-2">
              {retailer.economic_model === "Pricing-Led" 
                ? "90%+ of trade spend is on pricing depth; tools are secondary"
                : "Balanced approach combining pricing with tool investments"
              }
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 h-[88px]">
            <div className="p-4 bg-muted/30 flex flex-col justify-center">
              <p className="text-sm text-muted-foreground">Pricing Weight</p>
              <p className="text-xl font-bold">{retailer.economic_model === "Pricing-Led" ? "90%" : "60%"}</p>
            </div>
            <div className="p-4 bg-muted/30 flex flex-col justify-center">
              <p className="text-sm text-muted-foreground">Tool Weight</p>
              <p className="text-xl font-bold">{retailer.economic_model === "Pricing-Led" ? "10%" : "40%"}</p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-primary" />
            <h3 className="section-header">Promotion Cadence</h3>
          </div>
          <div className="text-center p-8 border border-border bg-muted/20 flex-grow flex flex-col justify-center">
            <p className="text-2xl font-bold text-foreground">{retailer.promo_cadence}</p>
            <p className="text-muted-foreground mt-2">Typical promotion rhythm</p>
          </div>
          <div className="mt-4 p-4 bg-muted/30 h-[88px] flex flex-col justify-center">
            <p className="text-sm text-muted-foreground">What this means</p>
            <p className="text-sm mt-1">
              {retailer.promo_cadence.includes("Weekly") 
                ? "High frequency promotions increase pantry loading risk. Discount depth should be capped."
                : "Seasonal + event focus allows for deeper, more impactful promotional investments."
              }
            </p>
          </div>
        </div>
      </div>

      {/* Contract Lock-in */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-primary" />
          <h3 className="section-header">Contract Lock-in Window</h3>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center p-6 border border-border">
            <p className="text-4xl font-bold text-foreground">{retailer.lock_in_window_days}</p>
            <p className="text-muted-foreground mt-2">Total Lock-in Days</p>
            <p className="text-xs text-muted-foreground">Full commitment window</p>
          </div>
          <div className="text-center p-6 border border-border">
            <p className="text-4xl font-bold text-warning">{retailer.pricing_lock_in_days}</p>
            <p className="text-muted-foreground mt-2">Pricing Lock-in</p>
            <p className="text-xs text-muted-foreground">Days before price is frozen</p>
          </div>
          <div className="text-center p-6 border border-border">
            <p className="text-4xl font-bold text-success">{retailer.tool_lock_in_days}</p>
            <p className="text-muted-foreground mt-2">Tool Lock-in</p>
            <p className="text-xs text-muted-foreground">More flexibility on tools</p>
          </div>
        </div>
      </div>

      {/* Margin & Reconciliation */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="h-5 w-5 text-primary" />
            <h3 className="section-header">Margin Structure</h3>
          </div>
          <div className="text-center p-8 flex-grow">
            <p className="text-5xl font-bold text-primary">{retailer.margin_structure}%</p>
            <p className="text-muted-foreground mt-2">Target Gross Margin</p>
          </div>
          <div className="mt-4 p-4 bg-muted/30 h-[80px] flex flex-col justify-center">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Margin Volatility</span>
              <span className={`font-bold ${retailer.margin_volatility <= 0.15 ? "text-success" : "text-warning"}`}>
                ±{formatPercent(retailer.margin_volatility)}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCcw className="h-5 w-5 text-primary" />
            <h3 className="section-header">Post-Event Reconciliation</h3>
          </div>
          <div className="text-center p-8 flex-grow">
            <p className="text-5xl font-bold text-foreground">{formatPercent(retailer.reconciliation_likelihood)}</p>
            <p className="text-muted-foreground mt-2">Likelihood of Refunds/Adjustments</p>
          </div>
          <div className="mt-4 p-4 bg-muted/30 h-[80px] flex flex-col justify-center">
            <p className="text-sm text-muted-foreground">CFO Note</p>
            <p className="text-sm mt-1 line-clamp-1">
              {retailer.reconciliation_likelihood > 0.1 
                ? "Higher reconciliation likelihood affects bottom line."
                : "Low reconciliation likelihood means more predictable margins."
              }
            </p>
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This profile anchors every promotion decision in <span className="text-primary font-bold">retailer reality</span>, not theory. 
          Understanding {retailer.name}'s economic model drives better investment choices.
        </p>
      </div>
    </div>
  );
};

export default RetailerEconomics;
